import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { CheckmarkCircleIcon, FilesIcon } from '@navikt/aksel-icons';
import type { SurveyProcess } from '../../../api/surveyProcess';
import { Routes } from '../../../enums/Routes';
import { ClipboardUtilsImpl } from '../../../utils/ClipboardUtils';
import {
  useSendActionPlanSummaryMutation,
  useSendReminderMutation,
} from '../../../hooks/queries/surveyProcess/useSurveyProcessQuery';
import {
  TimedText,
  asksToPlanNextSurvey,
  completedTitle,
  focusAreaOf,
  linkOf,
  nextSurveyOf,
  nowFacts,
  nowLabel,
  nowLead,
  nowTitle,
  reminderAction,
  pageOf,
  planCardOf,
  type PlanCard,
} from '../processView';
import classes from './SurveyProcess.module.css';

/** A text that holds a time translates the time first, then puts it in */
const useText = () => {
  const { t } = useTranslation();
  return (text: TimedText) =>
    t(text.key, { ...text.values, ...(text.time ? { time: t(text.time.key, text.time.values) } : {}) });
};

const useCopy = (url: string) => {
  const { t } = useTranslation();
  return () =>
    new ClipboardUtilsImpl({
      copySuccessText: t('surveyProcess.copied'),
      copyErrorText: t('surveyProcess.copyError'),
    }).copy(url);
};

const ShareLink = ({ label, url }: { label: string; url: string }) => {
  const { t } = useTranslation();
  const copy = useCopy(url);
  return (
    <div className={classes.link}>
      <span className={classes.linkText}>
        <Paragraph data-size="xs" className={classes.subtle}>
          {label}
        </Paragraph>
        <a href={url} target="_blank" rel="noreferrer" className={`ds-link ${classes.url}`}>
          {url}
        </a>
      </span>
      <Button variant="secondary" data-size="sm" onClick={copy}>
        <FilesIcon aria-hidden />
        {t('surveyProcess.copy')}
      </Button>
    </div>
  );
};

/** The action plan the team has, with what the team chose most, to copy or open */
const ActionPlanCard = ({ card }: { card: PlanCard }) => {
  const { t } = useTranslation();
  const say = useText();
  const copy = useCopy(card.url);
  return (
    <div className={`${classes.link} ${classes.plan}`}>
      <span className={classes.linkText}>
        <span className="ds-label" data-size="sm">
          {t('surveyProcess.planCard.title')}
        </span>
        {card.chosen.length > 0 && (
          <Paragraph data-size="xs" className={classes.subtle}>
            {card.chosen.map(say).join(' · ')}
          </Paragraph>
        )}
        <Paragraph data-size="xs" className={`${classes.subtle} ${classes.url}`}>
          {card.url}
        </Paragraph>
      </span>
      <span className={classes.actions}>
        <Button variant="tertiary" data-size="sm" onClick={copy}>
          {t('surveyProcess.copy')}
        </Button>
        <Button asChild variant="primary" data-size="sm">
          <a href={card.url} target="_blank" rel="noreferrer">
            {t('surveyProcess.links.openActionPlan')}
          </a>
        </Button>
      </span>
    </div>
  );
};

/** After the action plan, the next survey shows whether it worked. It comes second to the plan the team works on */
const NextSurvey = ({ process }: { process: SurveyProcess }) => {
  const { t, i18n } = useTranslation();
  const next = nextSurveyOf(process, i18n.language);
  if (!next) return null;
  return (
    <div className={`${classes.link} ${classes.nextSurvey}`}>
      <span className={classes.linkText}>
        <span className="ds-label" data-size="sm">
          {t('surveyProcess.facts.nextSurvey')}
        </span>
        <Paragraph data-size="sm">{t(next.text.key, next.text.values)}</Paragraph>
      </span>
      <Button asChild variant={next.planned ? 'tertiary' : 'secondary'} data-size="sm">
        <Link to={next.planned ? Routes.PlannedSurveys : Routes.CreateSurvey}>
          {t(next.planned ? 'surveyProcess.nextSurvey.change' : 'surveyProcess.planNextSurvey')}
        </Link>
      </Button>
    </div>
  );
};

const Reminder = ({ process, teamId }: { process: SurveyProcess; teamId: number }) => {
  const { t, i18n } = useTranslation();
  const say = useText();
  const remind = useSendReminderMutation(teamId, process.step);
  const action = reminderAction(process.now, i18n.language);
  if (!action) return null;
  if (!action.canSend) return <Paragraph data-size="sm">{say(action.text)}</Paragraph>;
  return (
    <div className={classes.actions}>
      <Button variant="secondary" data-size="sm" loading={remind.isPending} onClick={() => remind.mutate()}>
        {t(action.label)}
      </Button>
      {remind.isError && (
        <Paragraph data-size="sm" role="alert">
          {t('surveyProcess.reminder.error')}
        </Paragraph>
      )}
    </div>
  );
};

/** Trustpulse did not send the summary, so the team leader sends it, and is told it is on its way */
const SendSummary = ({ process, teamId }: { process: SurveyProcess; teamId: number }) => {
  const { t } = useTranslation();
  const send = useSendActionPlanSummaryMutation(teamId);
  if (send.isSuccess)
    return (
      <Paragraph data-size="sm" role="status">
        {t('surveyProcess.summary.requested')}
      </Paragraph>
    );
  if (!process.canSendSummary) return null;
  return (
    <div className={classes.actions}>
      <Button variant="secondary" data-size="sm" loading={send.isPending} onClick={() => send.mutate()}>
        {t('surveyProcess.summary.send')}
      </Button>
      {send.isError && (
        <Paragraph data-size="sm" role="alert">
          {t('surveyProcess.summary.error')}
        </Paragraph>
      )}
    </div>
  );
};

/**
 * Once the round is over, it says so above the folded steps. The action plan stays in focus until the next survey
 * starts the next round
 */
export const RoundCompleted = ({ process }: { process: SurveyProcess }) => {
  const { t } = useTranslation();
  const title = completedTitle(process);
  const planCard = planCardOf(process, window.location.origin);
  if (!title) return null;
  return (
    <section className={classes.completed} aria-labelledby="process-completed">
      <div className={classes.completedHeading}>
        <CheckmarkCircleIcon aria-hidden className={classes.completedIcon} />
        <Heading level={3} data-size="xs" id="process-completed">
          {t(title.key, title.values)}
        </Heading>
      </div>
      <Paragraph data-size="sm">{t('surveyProcess.completed.text')}</Paragraph>
      {planCard && <ActionPlanCard card={planCard} />}
      <NextSurvey process={process} />
    </section>
  );
};

/** What is happening now in the step the team is in, what the team leader can do, and the link the team needs */
export const ProcessNowCard = ({ process, teamId }: { process: SurveyProcess; teamId: number }) => {
  const { t, i18n } = useTranslation();
  const say = useText();
  const lead = nowLead(process);
  const link = linkOf(process, window.location.origin);
  const page = pageOf(process, teamId);
  const plansNext = asksToPlanNextSurvey(process.now);
  return (
    <div className={classes.now}>
      <div>
        <span className={`ds-label ${classes.nowLabel}`} data-size="sm">
          {say(nowLabel(process))}
        </span>
        <Heading level={3} data-size="xs">
          {say(nowTitle(process))}
        </Heading>
      </div>
      {lead && <Paragraph data-size="sm">{say(lead)}</Paragraph>}
      <dl className={classes.facts}>
        {process.actionPlan && (
          <div className={classes.fact}>
            <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
              {t('surveyProcess.focusArea')}
            </dt>
            <dd>
              <Paragraph data-size="sm">{focusAreaOf(process, t)}</Paragraph>
            </dd>
          </div>
        )}
        {nowFacts(process.now, i18n.language).map((fact) => (
          <div key={fact.id} className={classes.fact}>
            <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
              {t(fact.label)}
            </dt>
            <dd>
              <Paragraph data-size="sm">{say(fact.value)}</Paragraph>
              {fact.progress !== undefined && (
                <span
                  role="progressbar"
                  aria-label={t(fact.label)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={fact.progress}
                  className={classes.progress}
                >
                  <span className={classes.progressBar} style={{ width: `${fact.progress}%` }} />
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
      <Reminder process={process} teamId={teamId} />
      <SendSummary process={process} teamId={teamId} />
      {link && <ShareLink label={t(link.label)} url={link.url} />}
      {(page || plansNext) && (
        <div className={classes.actions}>
          {page && (
            <Button asChild variant="secondary" data-size="sm">
              <Link to={page.to}>{t(page.label)}</Link>
            </Button>
          )}
          {plansNext && (
            <Button asChild variant="secondary" data-size="sm">
              <Link to={Routes.CreateSurvey}>{t('surveyProcess.planNextSurvey')}</Link>
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
